import { Auth } from 'aws-amplify';
import { put, call, takeLatest, select } from 'redux-saga/effects';
import LDClient from 'ldclient-js';
import { getUserGroups } from '../utils/groups';
import ktRequest from '../utils/request';
import config from '../config';

function * getAndConfigureIdentity () {
  const profileResult = yield ktRequest.get('profile', 'identity');

  yield put({
    type: 'FETCH_PROFILE_SUCCESS',
    userId: profileResult.legacyUserId,
    profilePicture: profileResult.profilePicture,
    name: profileResult.name,
    email: profileResult.email,
    centers: profileResult.centers
  });

  let store = yield select();

  const lduser = {
    key: store.identity.email,
    email: store.identity.email,
    custom: {
      employeeCenterId: String(store.identity.centerId),
      isEmployee: true
    }
  };

  var ldclient = LDClient.initialize(config.launchDarkly.clientId, lduser);

  const ldPromise = new Promise((resolve, reject) => (ldclient.on('ready', () => resolve())));
  yield ldPromise;

  const enterpriseLoginAllowed = ldclient.variation('enterprise-app', false);
  if (!enterpriseLoginAllowed) {
    throw new Error('Your center does not yet support enterprise features');
  }

  const allFlags = ldclient.allFlags();
  yield put({
    type: 'SET_FEATURE_FLAG',
    flag: allFlags
  });
}

export function * setUserFromSession (payload) {
  try {
    yield call(getAndConfigureIdentity);

    // make sure we have room information
    yield put({ type: 'FETCH_ROOMS' });
  } catch (error) {
    console.log(error);
    // dispatch a failure action to the store with the error
    yield put({
      type: 'LOGIN_FAILURE',
      errorMessage: error.message
    });
  }
}

export function * login (payload) {
  try {
    let userPayload = payload.userPayload;
    const username = userPayload.username;
    const password = userPayload.password;

    // try to sign cognito user in
    let user = yield Auth.signIn(username, password);

    // First we have to check for Force Change Password
    if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
      yield put({
        type: 'NEW_PASSWORD_REQUIRED',
        user: user
      });
      return;
    }

    yield call(getAndConfigureIdentity);

    // dispatch a success action to the store with the logged in user
    yield put({
      type: 'LOGIN_SUCCESS',
      user: user
    });

    return;
  } catch (error) {
    console.log(error);
    // dispatch a failure action to the store with the error
    yield put({
      type: 'LOGIN_FAILURE',
      errorMessage: error.message
    });
  }
}

export function * signup (payload) {
  try {
    let userPayload = payload.userPayload;
    const username = userPayload.username;
    const password = userPayload.password;
    const confirmationCode = userPayload.confirmationCode;

    yield Auth.confirmSignUp(username, confirmationCode).catch(err =>
      console.error(err)
    );
    // adding user to 'Parent' group is handled via Lambda

    // successfully confirmed, now log the user in
    yield Auth.signIn(username, password);

    const userGroups = yield getUserGroups();
    userPayload = {
      ...userPayload,
      ...{
        group: userGroups[0]
      }
    };

    yield put({
      type: 'SIGNUP_SUCCESS',
      user: userPayload
    });
  } catch (error) {
    yield put({
      type: 'SIGNUP_FAILURE',
      errorMessage: error.message
    });
  }
}

export function * forgotPassword (payload) {
  try {
    let userPayload = payload.userPayload;
    const email = userPayload.email;

    yield Auth.forgotPassword(email);

    yield put({
      type: 'FORGOT_PASSWORD_SUCCESS',
      forgotPasswordEmail: email
    });
  } catch (error) {
    yield put({
      type: 'FORGOT_PASSWORD_FAILURE',
      errorMessage: error.message
    });
  }
}

export function * forgotPasswordSubmit (payload) {
  try {
    let userPayload = payload.userPayload;
    const newPassword = userPayload.password;
    const confirmationCode = userPayload.confirmationCode;

    let store = yield select();
    let email = store.authentication.forgotPasswordEmail;

    // changes password and logs user in
    yield Auth.forgotPasswordSubmit(email, confirmationCode, newPassword);

    yield put({
      type: 'FORGOT_PASSWORD_SUBMIT_SUCCESS',
      user: userPayload
    });
  } catch (error) {
    yield put({
      type: 'FORGOT_PASSWORD_SUBMIT_FAILURE',
      errorMessage: error.message
    });
  }
}

export function * changePassword (payload) {
  try {
    let userPayload = payload.userPayload;
    const newPassword = userPayload.password;

    let store = yield select();
    let user = store.authentication.user;

    // changes password and logs user in
    yield Auth.completeNewPassword(
      user,
      newPassword,
      user.challengeParam.requiredAttributes
    );

    yield put({
      type: 'FORGOT_PASSWORD_SUBMIT_SUCCESS',
      user: userPayload
    });
  } catch (error) {
    yield put({
      type: 'FORGOT_PASSWORD_SUBMIT_FAILURE',
      errorMessage: error.message
    });
  }
}

export function * logout () {
  try {
    yield Auth.signOut();
    yield put({
      type: 'LOGOUT'
    });
  } catch (error) {
    alert(error);
  }
}

export const ktAuthenticationSagas = [
  takeLatest('LOGIN_REQUEST', login),
  takeLatest('SET_USER_FROM_SESSION', setUserFromSession),
  takeLatest('SIGNUP_REQUEST', signup),
  takeLatest('FORGOT_PASSWORD_REQUEST', forgotPassword),
  takeLatest('FORGOT_PASSWORD_SUBMIT_REQUEST', forgotPasswordSubmit),
  takeLatest('CHANGE_PASSWORD_REQUEST', changePassword),
  takeLatest('LOGOUT_REQUEST', logout),
  takeLatest('CONFIGURE_IDENTITY', getAndConfigureIdentity) // re-configure launchdarkly on center switch
];
